import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  Tag,
  TagCloseButton,
  TagLabel,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio, RadioGroup,
  Select,
  Stack,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack,
  Wrap,
  WrapItem
} from '@chakra-ui/react';
import React, { useContext, useEffect, forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaPlusCircle } from "react-icons/fa";
import { RiDeleteBin6Fill } from "react-icons/ri";

import FileList from "../../components/FileList";
import { WebAPIContext } from '../../contexts/WebAPIContext';
import {
  CustomerNationality,
  formatPricingAgreementForm,
  Messages,
  WorkflowId,
} from "../../contexts/constants";
import {
  BilingualName,
  competitiorCondition,
  Competitor,
  competitorType,
  ConfirmorInfo,
  Errors,
  exchangeType,
  Material,
  materialType,
  MMProductCompetitor,
  NationalityType,
  OtherAgreementItem,
  PagePermissions,
  PricingAgreementFormType,
  pricingType,
  ProjectContextType,
  ProjectWithRdsData,
  quantitySubType,
  quantityType,
  uploadOptionType,
  LanguageCode,
} from '../../contexts/types';

import HelpIcon from "./HelpIcon";

import { useAppSelector } from "../../store/hooks";
import { useFileService } from "../../utils/FileService";
import { getMultilingualDisplayName, getBilingualDisplayName } from "../../utils/i18nDisplay";
import { setNestedValue } from "../../utils/nestedObject";
import { getConfirmor, updateConfirm } from "../../utils/workflow";
import ConfirmorList from "./ConfirmorList";
import { CustomNumberInput } from "./CustomNumberInput";

// コンテキスト
import { getPermissions, ProjectContext } from "./InvestmentScreeningDetail";

import { supportedLngs } from '../../utils/i18n';

export interface PricingAgreementFormHandle {
  getSnapshot: () => Record<string, unknown>;
  applySnapshot: (data: Record<string, unknown>) => void;
}

/**
 * 顧客取り決め事項パラメータ
 */
interface PricingAgreementFormProps {
  /** プロジェクトID */
  projectId: string;
}

/** SUPPORTED_LANGUAGES/supportedLngsに追従する動的言語リスト */
const langs = Object.keys(supportedLngs) as LanguageCode[];

/**
 * 確認担当者パラメータ
 */
interface Confirmor {
  pricingAgreementForm?: {
    companyDivision?: string[];
    salesDivision?: string[];
    procurementDivision?: string[];
  };
}

// カテゴリの比較キーを生成するヘルパー（ja/enの順序を明示的に固定）
const categoryKeyOf = (c?: BilingualName): string | undefined =>
  c ? JSON.stringify({ ja: c.ja, en: c.en }) : undefined;

/**
 * 新規投資申請画面(顧客取り決め事項)
 * @returns
 */
const PricingAgreementForm = forwardRef<PricingAgreementFormHandle, PricingAgreementFormProps>(({ projectId }, ref) => {

  const { t } = useTranslation();
  /** トースト表示用 */
  const toast = useToast();

  /** 通信オブジェクト */
  const WebAPI = useContext(WebAPIContext);

  /** 画面タイトル */
  const TITLE = t('features.InvestmentScreening.PricingAgreementForm.title');

  /** 保存禁止(例外発生時など) */
  const [hasfetchException, setHasfetchException] = useState(false);

  /** 権限用 画面パーミッション */
  const [pagePermissions, setPagePermissions] = useState<PagePermissions>();

  // -----------------------------
  // 権限情報
  // -----------------------------
  const { userPermissions, reviewDate } = useContext(ProjectContext) as ProjectContextType;

  /** ファイル操作用（振り返りモード時は reviewDate を渡して振り返り分も含めた累積取得を行う） */
  const { uploadedFiles, fetchFiles, handleFileSelect, uploadFiles, handleDelete } = useFileService(TITLE, `${projectId}/PricingAgreementForm`, reviewDate ?? undefined);

  /** 入力項目名幅 */
  const labelWidth = 155;

  /** 最終確認ラベルスタイル */
  const finalConfirmLabelStyle = {
    fontWeight: "bold",
    width: `180px`,
  };

  /** 最終確認値スタイル */
  const finalConfirmValueStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }

  /** エラーメッセージスタイル */
  const errorMessageStyle = {
    marginTop: "0px",
    marginLeft: "0px",
  }

  /** ラベルスタイル */
  const labelStyle = {
    fontWeight: "bold",
    marginTop: "15px",
  }

  /** 材料選択値 */
  const [tempSelectedMaterials, setTempSelectedMaterials] = useState<string[]>([]);
  /** その他材料（一時値） */
  const [tempOtherMaterials, setTempOtherMaterials] = useState<BilingualName[]>([]);
  /** 材料選択 */
  const { isOpen: isMaterialModalOpen, onOpen: onMaterialModalOpen, onClose: onMaterialModalClose } = useDisclosure();
  /** 材料情報 */
  const [materials, setMaterials] = useState<Material[]>([]);

  // 確認モーダル
  const { isOpen: isConfirmationModalOpen, onOpen: onConfirmationModalOpen, onClose: onConfirmationModalClose } = useDisclosure();

  /** ログインユーザー：従業員ID */
  const employeeId = useAppSelector((state) => state.employee.employeeId);
  /** ログインユーザー：部署ID */
  const divisionId = useAppSelector((state) => state.employee.divisionId);

  /**
   * 単価取り決め事項を初期化する。
   */
  const getClearPricingAgreementFormValue = () => {
    return {
      pricing: {
        /** 単価の取り決め選択 */
        pricingType: pricingType.FirstTimeTransactionOnly.value,
        /** 一定期間（ｎヶ月間） */
        pricingDuration: null,
        /** 自由記入欄 */
        pricingComment: null,
      },
      /** 為替変動の取り決め */
      exchange: {
        /** 為替変動の取り決め選択 */
        exchangeType: exchangeType.SubjectToFluctuations.value,
        /** 見直し（ｎヶ月に1度） */
        exchangeDuration: null,
        /** 見直し（パーセント） */
        exchangeThreshold: null,
        /** 自由記入欄 */
        exchangeComment: null,
      },
      /** 材料費変動の取り決め */
      material: {
        /** 材料費変動の取り決め選択 */
        materialType: materialType.SubjectToFluctuations.value,
        /** 材料選択 */
        selectedMaterials: [],
        /** その他材料（自由入力） */
        otherMaterials: [],
        /** 自由記入欄 */
        materialComment: null,
      },
      /** 数量の取り決め */
      quantity: {
        /** 数量の取り決め選択 */
        quantityType: quantityType.NoArrangement.value,
        /** 数量未達時の取り決め選択 */
        quantitySubType: quantitySubType.Preliminary.value,
        /** 内示（ｎヶ月） */
        quantityDuration: null,
        /** 数量未達時の自由記入欄 */
        notReachedComment: null,
        /** 自由記入欄 */
        quantityComment: null,
      },
      /** 競合の有無 */
      competitor: {
        /** 競合の有無選択 */
        competitorType: competitorType.NoCompetitor.value,
        /** 競合状態 */
        competitiorCondition: competitiorCondition.TopRank.value,
        /** 競合会社選択 */
        selectedCompetitors: [],
        /** 競合会社名（OTHER） */
        competitorNameOther: null,
        /** 当社及び競合のシェア */
        shareOfCompetitorAndCompany: null,
        /** 当社シェア（0～100%） */
        sharePercentage: null,
      },
      /** ファイルアップロードオプション */
      uploadOption: true,
    } as PricingAgreementFormType;
  }

  /**
   * エラー情報を初期化する。
   */
  const clearErrors = () => {
    setErrors(JSON.parse(JSON.stringify({
      pricingDuration: {
        isError: false,
        errorMessage: ""
      },
      pricingComment: {
        isError: false,
        errorMessage: ""
      },
      exchangeDuration: {
        isError: false,
        errorMessage: ""
      },
      exchangeThreshold: {
        isError: false,
        errorMessage: ""
      },
      exchangeComment: {
        isError: false,
        errorMessage: ""
      },
      selectedMaterials: {
        isError: false,
        errorMessage: ""
      },
      materialComment: {
        isError: false,
        errorMessage: ""
      },
      quantityDuration: {
        isError: false,
        errorMessage: ""
      },
      quantityComment: {
        isError: false,
        errorMessage: ""
      },
      selectedCompetitors: {
        isError: false,
        errorMessage: ""
      },
      sharePercentage: {
        isError: false,
        errorMessage: ""
      },
      selectedUploadFiles: {
        isError: false,
        errorMessage: ""
      },
    })));
  }

  /** プロジェクト情報*/
  const [projectInfo, setProjectInfo] = useState<ProjectWithRdsData>();

  /** 顧客取り決め事項 情報 */
  const [pricingAgreementFormValue, setPricingAgreementFormValue] = useState<PricingAgreementFormType>(getClearPricingAgreementFormValue());

  /** 顧客国籍 */
  const [customerNationality, setCustomerNationality] = useState<string>();

  /** 国籍マスタ一覧 */
  const [nationalityList, setNationalityList] = useState<NationalityType[]>([]);

  /** 確認モーダル表示情報 */
  const [confirmData, setConfirmData] = useState<any>();

  /** エラー情報 */
  const [errors, setErrors] = useState<Errors>();

  /** どの部署の確認かを表す */
  const [confirmSource, setConfirmSource] = useState<string>("");

  /** 確認担当者 */
  const [confirmor, setConfirmor] = useState<Confirmor | null>(null);

  /** 確認ボタン横幅 */
  const [buttonWidths, setButtonWidths] = useState<Record<string, number>>({});

  /** 確認ボタン下担当者名 */
  const [names, setNames] = useState<Record<string, ConfirmorInfo[]>>({
    companyDivision: [],
    salesDivision: [],
    procurementDivision: [],
  });

  /** 確認ボタンRef */
  const refs: Record<string, React.RefObject<HTMLButtonElement>> = {
    companyDivision: useRef<HTMLButtonElement>(null),
    companyHeadDivision: useRef<HTMLButtonElement>(null),
    salesDivision: useRef<HTMLButtonElement>(null),
    procurementDivision: useRef<HTMLButtonElement>(null),
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  // 編集モーダル保存ボタンの活性制御
  const [isSaving, setIsSaving] = useState(false);

  // OTHERボタン名
  const OTHER = "OTHER";

  // 競合未確認ボタン名
  const UNCONFIRMED = "UNCONFIRMED";

  // M_競合（M_COMPETITOR）
  const [competitor, setCompetitor] = useState<Competitor[]>([]);

  // MM製品カテゴリに紐づく競合リスト（r_mm_product.competitor_id_list）
  const [mmProductCompetitor, setMmProductCompetitor] = useState<MMProductCompetitor>({});

  // 事業部ごとの競合会社（ボタン表示用）
  const [competitorCandidatesForDept, setCompetitorCandidatesForDept] = useState<Competitor[]>([]);

  // 選択中の競合会社（競合ID）
  const [selectedCompetitorIds, setSelectedCompetitorIds] = useState<string[]>([]);

  // その他の取り決め
  const [otherAgreements, setOtherAgreements] = useState<OtherAgreementItem[]>([]);

  // 一時保存対象（3項目）: pricingAgreementFormValue / otherAgreements / customerNationality
  //   ※ アップロードファイル本体は保存しない。未送信ファイル名のみ記録し、復元時に再選択を案内する
  useImperativeHandle(ref, () => ({
    getSnapshot: () => {
      const data: Record<string, unknown> = {};
      data.pricingAgreementFormValue = pricingAgreementFormValue;
      if (otherAgreements.length > 0) data.otherAgreements = otherAgreements;
      if (customerNationality !== undefined) data.customerNationality = customerNationality;
      // 選択済みだがS3未送信のファイル名を記録（復元時の案内用）
      const pendingFileNames = uploadedFiles.filter(f => f.file).map(f => f.fileName);
      if (pendingFileNames.length > 0) data.pendingFileNames = pendingFileNames;
      return data;
    },
    applySnapshot: (data: Record<string, unknown>) => {
      if (data.pricingAgreementFormValue !== undefined)
        setPricingAgreementFormValue(data.pricingAgreementFormValue as PricingAgreementFormType);
      if (data.otherAgreements !== undefined)
        setOtherAgreements(data.otherAgreements as OtherAgreementItem[]);
      if (data.customerNationality !== undefined)
        setCustomerNationality(data.customerNationality as string);
      if (Array.isArray(data.pendingFileNames) && data.pendingFileNames.length > 0) {
        toast({
          title: TITLE,
          description: `以下のファイルは一時保存に含まれないため、再選択が必要です：${(data.pendingFileNames as string[]).map(n => `「${n}」`).join('、')}`,
          status: 'warning',
          duration: 8000,
          isClosable: true,
        });
      }
    },
  }));

  /**
   * MM製品カテゴリごとの競合会社（ボタン表示用）初期化
   */
  useEffect(() => {
    // 使用データ存在チェック
    const hasMMProductCategoryId = !!projectInfo?.mmProductCategoryId;
    const hasCompetitor = Array.isArray(competitor) && competitor.length > 0;
    const hasMmProductCompetitor
      = Array.isArray(mmProductCompetitor?.competitorList) && mmProductCompetitor.competitorList.length > 0;
    if (!hasMMProductCategoryId || !hasCompetitor || !hasMmProductCompetitor) {
      // 使用データが不足している場合は、処理を抜ける
      setCompetitorCandidatesForDept([]);
      return;
    }

    // MM製品カテゴリごとの競合会社取得
    const matchedCompetitors = competitor.filter(comp => {
      if (!comp.competitorId) return false;
      return (mmProductCompetitor.competitorList || []).includes(comp.competitorId);
    });

    // 名前順に並べ替え
    const sortedCompetitors = matchedCompetitors.sort((a, b) => {
      const nameA = a.competitorName || '';
      const nameB = b.competitorName || '';
      return nameA.localeCompare(nameB);
    });

    // MM製品カテゴリごとの競合会社（ボタン表示用）を更新
    setCompetitorCandidatesForDept(sortedCompetitors);
  }, [projectInfo?.mmProductCategoryId, competitor, mmProductCompetitor]);

  /**
   * 選択中の競合会社（競合ID）初期化
   */
  useEffect(() => {
    // 使用データ存在チェック
    const hasCompetitor = Array.isArray(competitor) && competitor.length > 0;
    const hasSelectedCompetitor
      = Array.isArray(pricingAgreementFormValue?.competitor?.selectedCompetitors)
      && pricingAgreementFormValue?.competitor?.selectedCompetitors.length > 0;
    if (!hasCompetitor || !hasSelectedCompetitor) {
      // 使用データが不足している場合は、処理を抜ける
      setSelectedCompetitorIds([]);
      return;
    }

    // 選択中の競合会社取得
    const selectedCompetitor = pricingAgreementFormValue?.competitor?.selectedCompetitors;
    const validCompetitorIds = selectedCompetitor.filter(
      (id): id is string => id !== undefined
    );

    // 選択中の競合会社（競合ID）を更新
    setSelectedCompetitorIds(validCompetitorIds);
  }, [pricingAgreementFormValue?.competitor?.selectedCompetitors, competitor]);

  /**
   * 権限情報
   */
  useEffect(() => {
    const pagePermissions = getPermissions(userPermissions, WorkflowId.PricingAgreementForm, projectInfo?.purpose, employeeId, divisionId, projectInfo?.registrantDivisionId);
    setPagePermissions(pagePermissions);
  }, [userPermissions, employeeId, divisionId, projectInfo]);

  /**
   * 初期表示時
   */
  useEffect(() => {
    if (projectId && projectId !== "") {

      fetchData(false);

    } else {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.PricingAgreementForm.message.registerFromBasicInfo'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }
  }, []);

  useEffect(() => {
    const widths: Record<string, number> = Object.keys(refs).reduce((acc, key) => {
      acc[key] = refs[key]?.current?.offsetWidth || 0;
      return acc;
    }, {} as Record<string, number>);
    setButtonWidths(widths);
  }, []);

  useEffect(() => {
    const fetchConfirmor = async () => {
      try {
        // 確認担当者JSONを取得
        const confirmorData = await getConfirmor(
          WebAPI,
          projectId,
          reviewDate,
        );
        setConfirmor(confirmorData);
      } catch (error) {
        // TODO 処理失敗時は暫定でスルーする
      }
    };
    if (projectId) {
      fetchConfirmor();
    }
  }, [projectId, reviewDate]);

  useEffect(() => {
    if (confirmor?.pricingAgreementForm) {
      const updatedNames = Object.keys(names).reduce((acc, division) => {
        const divisionData = confirmor.pricingAgreementForm?.[division as keyof Confirmor['pricingAgreementForm']];
        if (Array.isArray(divisionData)) {
          acc[division] = divisionData;
        } else {
          acc[division] = [];
        }
        return acc;
      }, {} as Record<string, ConfirmorInfo[]>);
      setNames(updatedNames);
    }
  }, [confirmor]);

  /**
   * プロジェクト情報を取得する。
   * @param isProjectOnly true：プロジェクト情報のみ取得、false：すべて取得
   */
  const fetchData = async (isProjectOnly: boolean) => {

    let query = `
          getProjectById(projectId: $projectId, useRDSData: $useRDSData, reviewDate: $reviewDate) {
            projectId
            pricingAgreement
            customerNationality
            purpose
            updateDate
            opportunityName
            revisedOpportunityNameByRevision
            registrantDivisionId
            divisionId
            mmProductCategoryId
            opportunityId
          }`;

    if (!isProjectOnly) {
      query += `
          getMaterial(hasDeleted: true) {
            materialId
            materialName { ${langs.join(' ')} }
            category { ${langs.join(' ')} }
            sortOrder
            deleted
          }`;
      query += `
          getCompetitor(hasDeleted: $hasDeleted) {
            competitorId
            competitorName
            deleted
          }`;
      query += `
          getNationality(hasDeleted: $hasDeleted) {
            nationalityId
            nationalityNameJpn
            nationalityNameEng
            order
          }`;
    }

    const variablesDef = isProjectOnly
      ? `($projectId: String, $useRDSData: Boolean, $reviewDate: String)`
      : `($projectId: String, $useRDSData: Boolean, $hasDeleted: Boolean, $reviewDate: String)`;

    const payload = {
      query: `
        query fetchPricingAgreementData${variablesDef} {
          ${query}
        }`,
      variables: {
        projectId: projectId,
        useRDSData: true,
        ...(!isProjectOnly && { hasDeleted: false }),
        ...(reviewDate && { reviewDate })
      }
    };

    // プロジェクト情報取得（dynamodb、RDS）
    const response = await WebAPI.request(payload);

    if (response && response.data && response.data.getProjectById) {
      if (!response.data.getProjectById.projectId) {
        // データ取得失敗の場合
        showApiError(t('features.InvestmentScreening.PricingAgreementForm.dataLabel.projectInfo'), "getProjectById", response.errors);
        setHasfetchException(true);
        return;
      }

      // プロジェクト情報
      const projectInfo = response.data.getProjectById;

      setProjectInfo(projectInfo);

      if (projectInfo.pricingAgreement && projectInfo.pricingAgreement !== "") {
        // 単価契約（合意）JSON ⇒ Object変換
        const pricingAgreement: PricingAgreementFormType = JSON.parse(projectInfo.pricingAgreement);
        if (![quantityType.Arrangement.value, quantityType.NoArrangement.value, quantityType.Other.value].includes(pricingAgreement.quantity.quantityType)) {
          // 既存データで取り決めあり/なし以外の場合は、なしに仮設定する。
          pricingAgreement.quantity.quantityType = quantityType.NoArrangement.value;
        }
        if (!pricingAgreement.competitor) {
          // 既存データで競合あり/なし以外の場合は、なしに仮設定する。
          pricingAgreement.competitor = {
            competitorType: competitorType.NoCompetitor.value,
            competitiorCondition: competitiorCondition.TopRank.value,
            selectedCompetitors: [],
            competitorNameOther: null,
            shareOfCompetitorAndCompany: null,
            sharePercentage: null,
          };
        }
        if (!pricingAgreement.competitor.competitiorCondition) {
          pricingAgreement.competitor.competitiorCondition = competitiorCondition.TopRank.value;
        }
        // 既存データにsharePercentageが無い場合の補完
        if (pricingAgreement.competitor.sharePercentage === undefined) {
          pricingAgreement.competitor.sharePercentage =
            pricingAgreement.competitor.competitorType === competitorType.Competitor.value ? null : 100;
        }

        // 既存データのotherMaterials（旧形式：文字列配列）をBilingualName配列に正規化
        if (Array.isArray(pricingAgreement.material?.otherMaterials)) {
          pricingAgreement.material.otherMaterials = pricingAgreement.material.otherMaterials.map((item: any) =>
            typeof item === "string"
              ? langs.reduce((acc, lang) => { acc[lang] = lang === langs[0] ? item : ""; return acc; }, {} as BilingualName)
              : item
          );
        }

        // 保存データがある場合は保存データを優先（営業支援案件の競合による初期選択は行わない）
        setPricingAgreementFormValue(pricingAgreement);
        // その他の取り決めを復元
        if (pricingAgreement.otherAgreements) {
          setOtherAgreements(pricingAgreement.otherAgreements);
        } else {
          setOtherAgreements([]);
        }
      } else {
        // 単価契約（合意）の登録なし ⇒ オブジェクト新規作成
        const newForm = getClearPricingAgreementFormValue();

        // 営業支援案件の競合会社で初期選択（未保存時のみ）
        if (projectInfo.opportunityId) {
          try {
            const oppResponse = await WebAPI.request({
              query: `
                query getOpportunityById($opportunityId: String) {
                  getOpportunityById(opportunityId: $opportunityId) {
                    competitorId
                  }
                }`,
              variables: { opportunityId: projectInfo.opportunityId }
            });
            const oppCompetitorId = oppResponse?.data?.getOpportunityById?.competitorId;
            if (oppCompetitorId) {
              const oppCompIds = oppCompetitorId.replace(/[{}]/g, '').split(',').filter((id: string) => id.trim() !== '');
              if (oppCompIds.length > 0) {
                newForm.competitor = {
                  ...newForm.competitor,
                  competitorType: competitorType.Competitor.value,
                  selectedCompetitors: oppCompIds,
                };
              }
            }
          } catch (e) {
            console.error('営業支援案件の競合会社取得エラー:', e);
          }
        }

        setPricingAgreementFormValue(newForm);
        setOtherAgreements([]);
      }

      // 顧客国籍設定
      setCustomerNationality(projectInfo.customerNationality ?? CustomerNationality.Japan.code);
      console.log("projectInfo.getNationality", projectInfo.getNationality);

      if (!isProjectOnly && projectInfo?.mmProductCategoryId) {
        const payload = {
          query: `
            query getMMProductById($MMProductCategoryId: String) {
              getMMProductById(MMProductCategoryId: $MMProductCategoryId) {
                MMProductCategoryId
                competitorIdList
              }
            }`,
          variables: { MMProductCategoryId: projectInfo.mmProductCategoryId }
        };

        // 競合会社情報取得（MM製品カテゴリ経由）
        const response = await WebAPI.request(payload);

        if (response?.data?.getMMProductById) {
          const data = response.data.getMMProductById;
          if (data.competitorIdList) {
            // 競合会社情報設定
            setMmProductCompetitor({ competitorList: data.competitorIdList });
          }
          // 0件の場合は何もしない（エラーにしない）
        } else if (response?.errors) {
          showApiError(t('features.InvestmentScreening.PricingAgreementForm.dataLabel.competitorInfo'), "getMMProductById", response.errors);
        }
      }

      // アップロード済みファイル情報取得（S3）
      await fetchFiles();

    } else {
      // データ取得失敗の場合
      showApiError(t('features.InvestmentScreening.PricingAgreementForm.dataLabel.projectInfo'), "getProjectById", response.errors);
      setHasfetchException(true);
    }

    if (!isProjectOnly) {
      if (response && response.data && response.data.getMaterial) {
        // 材料情報セット
        setMaterials(response.data.getMaterial);
      } else {
        showApiError(t('features.InvestmentScreening.PricingAgreementForm.dataLabel.materialInfo'), "getMaterial", response.errors);
      }
      if (response && response.data && response.data.getCompetitor) {
        // 競合会社情報セット
        setCompetitor(response.data.getCompetitor);
      } else {
        showApiError(t('features.InvestmentScreening.PricingAgreementForm.dataLabel.competitorInfo'), "getCompetitor", response.errors);
      }
      if (response && response.data && response.data.getNationality) {
        // 国籍マスタ情報セット
        setNationalityList(response.data.getNationality);
      } else {
        showApiError(t('features.InvestmentScreening.PricingAgreementForm.dataLabel.nationalityInfo'), "getNationality", response.errors);
      }
    }
  }

  /**
   * エラー情報をユーザー通知する
   * @param dataName データ論理名
   * @param apiName API名称
   * @param responseErrors レスポンスエラー情報
   */
  const showApiError = (dataName: string, apiName: string, responseErrors: any) => {
    toast({
      title: t('features.InvestmentScreening.PricingAgreementForm.message.dataFetchErrorTitle'),
      description: t('features.InvestmentScreening.PricingAgreementForm.message.fetchFailed', { name: dataName }),
      status: 'error',
      duration: 5000,
      isClosable: true,
    });

    console.error(`Failed to call API ${apiName}. errors = ${JSON.stringify(responseErrors)}`);
  }

  /**
   * 顧客取り決め事項 情報を変更する。
   * @param path 更新項目パス
   * @param newValue 値
   */
  const changePricingAgreementFormValue = (path: string, newValue: any) => {
    // ネスト階層のイミュータブル更新ロジックは src/utils/nestedObject.ts の
    // setNestedValue に切り出し済み（FE-BUG-12対応方針・単体テスト参照）。
    setPricingAgreementFormValue(prevState => setNestedValue(prevState, path, newValue));
  };

  /**
   * 材料選択モーダルを表示する。
   */
  const handleOpenMaterialModal = async () => {
    setTempSelectedMaterials(pricingAgreementFormValue.material.selectedMaterials);
    setTempOtherMaterials(pricingAgreementFormValue.material.otherMaterials || []);
    onMaterialModalOpen();
  }

  /**
   * 材料選択時に一時選択に反映する。
   * @param material 選択した材料
   */
  const handleMaterialSelection = (material: string) => {
    setTempSelectedMaterials((prev) =>
      prev.includes(material)
        ? prev.filter((item) => item !== material)
        : [...prev, material]
    );
  };

  /** BilingualNameのいずれかの言語に値が入っているか判定 */
  const hasAnyLangValue = (v?: BilingualName): boolean =>
    langs.some((lang) => (v?.[lang] ?? '').trim() !== '');

  /**
   * 材料選択を確定する。
   */
  const handleConfirmMaterialSelection = () => {
    setPricingAgreementFormValue((prevState) => ({
      ...prevState,
      material: {
        ...prevState.material,
        selectedMaterials: tempSelectedMaterials,
        otherMaterials: tempOtherMaterials.filter(hasAnyLangValue),
      },
    }));
    onMaterialModalClose();
  }

  /**
   * 入力値のバリデーションを行う。
   */
  const validate = () => {
    // 必須入力チェック
    const required = (value: number | null) => value;
    // エラー情報設定
    const changeErrors = (name: string, errorMessage: string) => {
      setErrors((prevState) => ({
        ...prevState,
        [name]: { isError: true, errorMessage: `${errorMessage}` },
      }));
      isError = true;
    };

    const requiredMessage = t('features.InvestmentScreening.PricingAgreementForm.validation.required');
    clearErrors();

    let isError = false;

    // 必須：単価の取り決め
    if (pricingAgreementFormValue.pricing.pricingType === pricingType.FixedPeriod.value) {
      if (!required(pricingAgreementFormValue.pricing.pricingDuration)) {
        changeErrors("pricingDuration", `${requiredMessage}`);
      }
    } else if (pricingAgreementFormValue.pricing.pricingType === pricingType.Other.value) {
      // 「上記以外」の場合、「自由記入欄」は必須入力
      if (!(pricingAgreementFormValue.pricing.pricingComment || null)) {
        changeErrors("pricingComment", `${requiredMessage}`);
      }
    }

    // 必須：為替変動の取り決め
    if (pricingAgreementFormValue.exchange.exchangeType === exchangeType.SubjectToFluctuations.value) {
      if (!required(pricingAgreementFormValue.exchange.exchangeDuration)) {
        changeErrors("exchangeDuration", `${requiredMessage}`);
      }
      if (!required(pricingAgreementFormValue.exchange.exchangeThreshold)) {
        changeErrors("exchangeThreshold", `${requiredMessage}`);
      }
    } else if (pricingAgreementFormValue.exchange.exchangeType === exchangeType.Other.value) {
      // 「上記以外」の場合、「自由記入欄」は必須入力
      if (!(pricingAgreementFormValue.exchange.exchangeComment || null)) {
        changeErrors("exchangeComment", `${requiredMessage}`);
      }
    }

    // 必須：材料費変動の取り決め
    if (pricingAgreementFormValue.material.materialType === materialType.SubjectToFluctuations.value) {
      if (pricingAgreementFormValue.material.selectedMaterials.length === 0 && (!pricingAgreementFormValue.material.otherMaterials || pricingAgreementFormValue.material.otherMaterials.filter(hasAnyLangValue).length === 0)) {
        changeErrors("selectedMaterials", `${requiredMessage}`);
      }
    } else if (pricingAgreementFormValue.material.materialType === materialType.Other.value) {
      // 「上記以外」の場合、「自由記入欄」は必須入力
      if (!(pricingAgreementFormValue.material.materialComment || null)) {
        changeErrors("materialComment", `${requiredMessage}`);
      }
    }

    // 必須：数量の取り決め
    if (pricingAgreementFormValue.quantity.quantityType === quantityType.Arrangement.value) {
      if (pricingAgreementFormValue.quantity.quantitySubType === quantitySubType.Preliminary.value) {
        if (!required(pricingAgreementFormValue.quantity.quantityDuration)) {
          changeErrors("quantityDuration", `${requiredMessage}`);
        }
      }
    } else if (pricingAgreementFormValue.quantity.quantityType === quantityType.Other.value) {
      // 「上記以外」の場合、「自由記入欄」は必須入力
      if (!(pricingAgreementFormValue.quantity.quantityComment || null)) {
        changeErrors("quantityComment", `${requiredMessage}`);
      }
    }

    // 必須：競合の有無
    if (pricingAgreementFormValue.competitor.competitorType === competitorType.Competitor.value) {
      if (pricingAgreementFormValue.competitor.selectedCompetitors.length === 0) {
        changeErrors("selectedCompetitors", `${requiredMessage}`);
      } else if (selectedCompetitorIds.includes(OTHER) && !pricingAgreementFormValue.competitor.competitorNameOther) {
        changeErrors("selectedCompetitors", `${requiredMessage}`);
      }
      // 当社シェアのバリデーション
      if (pricingAgreementFormValue.competitor.sharePercentage === null || pricingAgreementFormValue.competitor.sharePercentage === undefined) {
        changeErrors("sharePercentage", `${requiredMessage}`);
      } else if (pricingAgreementFormValue.competitor.sharePercentage < 0 || pricingAgreementFormValue.competitor.sharePercentage > 100) {
        changeErrors("sharePercentage", t('features.InvestmentScreening.PricingAgreementForm.validation.sharePercentageRange'));
      }
    }

    // 必須：アップロードファイルの有無
    if (pricingAgreementFormValue?.uploadOption ?? true) {
      if (uploadedFiles.length === 0) {
        changeErrors("selectedUploadFiles", t('features.InvestmentScreening.PricingAgreementForm.validation.fileRequired'));
      }
    }

    return !isError;
  }

  /**
   * 取り決め内容の確認モーダルを表示する。
   */
  const handleConfirm: React.MouseEventHandler<HTMLButtonElement> = (event) => {

    event.currentTarget.blur();

    // どの部署からの確認かを保持
    const { name } = event.target as HTMLButtonElement;
    setConfirmSource(name);

    if (!projectId || projectId === "") {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.PricingAgreementForm.message.registerFromBasicInfo'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!validate()) {
      toast({
        title: t('common.label.inputError'),
        description: t('features.InvestmentScreening.PricingAgreementForm.validation.inputError'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    // 確認モーダル用にフォーマット
    const pricingAgreementFormFormat = formatPricingAgreementForm(pricingAgreementFormValue, materials, competitor);
    setConfirmData(pricingAgreementFormFormat);

    // 確認モーダル表示
    onConfirmationModalOpen();
  }

  // ===== その他の取り決め ハンドラー =====
  /** その他の取り決め 追加 */
  const addOtherAgreement = () => {
    setOtherAgreements(prev => [...prev, { title: "", content: "" }]);
  };

  /** その他の取り決め 削除 */
  const removeOtherAgreement = (index: number) => {
    setOtherAgreements(prev => prev.filter((_, i) => i !== index));
  };

  /** その他の取り決め タイトル変更 */
  const changeOtherAgreementTitle = (index: number, value: string) => {
    setOtherAgreements(prev =>
      prev.map((item, i) => i === index ? { ...item, title: value } : item)
    );
  };

  /** その他の取り決め 内容変更 */
  const changeOtherAgreementContent = (index: number, value: string) => {
    setOtherAgreements(prev =>
      prev.map((item, i) => i === index ? { ...item, content: value } : item)
    );
  };

  /**
   * 保存処理を行う。
   */
  const handleSave = async (e: React.MouseEvent<HTMLButtonElement>) => {

    // フォーカスを外す
    e.currentTarget.blur();

    if (isSaving) return;
    setIsSaving(true);

    const tempData: PricingAgreementFormType = JSON.parse(JSON.stringify(pricingAgreementFormValue));

    // 単価の取り決め
    if (tempData.pricing.pricingType !== pricingType.FixedPeriod.value) {
      tempData.pricing.pricingDuration = null;
    }

    // 為替変動の取り決め
    if (tempData.exchange.exchangeType !== exchangeType.SubjectToFluctuations.value) {
      tempData.exchange.exchangeDuration = null;
      tempData.exchange.exchangeThreshold = null;
    }

    // 材料費変動の取り決め
    if (tempData.material.materialType !== materialType.SubjectToFluctuations.value) {
      tempData.material.selectedMaterials = [];
      tempData.material.otherMaterials = [];
    }
    // 旧フィールド(otherMaterial)をクリア（otherMaterialsに移行済み）
    delete (tempData.material as any).otherMaterial;

    // 数量の取り決め
    if (tempData.quantity.quantityType === quantityType.Arrangement.value) {
      //  「数量の取り決め」ありの場合
      if (tempData.quantity.quantitySubType !== quantitySubType.Preliminary.value) {
        // 「内示」以外の場合
        tempData.quantity.quantityDuration = null;
        tempData.quantity.notReachedComment = null;
      }
    } else {
      //  「数量の取り決め」なしの場合
      tempData.quantity.quantitySubType = null;
      tempData.quantity.quantityDuration = null;
      tempData.quantity.notReachedComment = null;
    }

    // 競合の有無
    if (tempData.competitor.competitorType !== competitorType.Competitor.value) {
      tempData.competitor.selectedCompetitors = [];
      tempData.competitor.competitorNameOther = null;
      tempData.competitor.shareOfCompetitorAndCompany = null;
      tempData.competitor.competitiorCondition = null;
      tempData.competitor.sharePercentage = 100; // 競合なし時は100%固定
    } else if (!tempData.competitor.selectedCompetitors.includes(OTHER)) {
      tempData.competitor.competitorNameOther = null;
    }

    // その他の取り決め（空の項目を除外して保存）
    tempData.otherAgreements = otherAgreements.filter(
      item => item.title.trim() !== "" || item.content.trim() !== ""
    );

    // GraphQLクエリ定義
    const query = `
      mutation updatePricingAgreement($input: PricingAgreementInput!) {
          updatePricingAgreement(input: $input) {status}
      }
    `;

    // APIリクエスト実行
    const result = await WebAPI.request({
      query: query,
      variables: {
        input: {
          projectId: projectId,
          pricingAgreement: JSON.stringify(tempData),
          customerNationality: customerNationality,
          updateDate: projectInfo?.updateDate,
          ...(reviewDate && { reviewDate }),
        }
      }
    });

    // 成功時処理
    if (result?.data?.updatePricingAgreement.status === 'success') {

      // ファイルをアップロード
      await uploadFiles();

      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.PricingAgreementForm.message.saveSuccess'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      try {
        // ワークフローテーブルを更新
        await updateConfirm(
          WebAPI,
          projectId,
          WorkflowId.PricingAgreementForm,
          employeeId,
          confirmSource,
          "true",
          reviewDate,
        );
        // 確認担当者JSONを取得
        const confirmor = await getConfirmor(
          WebAPI,
          projectId,
          reviewDate,
        );
        setConfirmor(confirmor);
      } catch (error) {
        // TODO 処理失敗時は暫定でスルーする
      }

      setIsSaving(false);
      onConfirmationModalClose();

      // データ再取得
      fetchData(true);
    } else if (result?.data?.updatePricingAgreement.status === 'expired') {
      toast({
        title: TITLE,
        description: Messages.expiredMessage,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      console.warn(`Conflict error. updatePricingAgreement.`);
    } else {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.PricingAgreementForm.message.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(`Failed to call API updatePricingAgreement. errors = ${JSON.stringify(result?.errors)}`);
    }
  }

  /**
   * レイアウト
   */
  return (
    <Box
      position="fixed"
      top="10vh"
      ml="2vw"
      mr="2vw"
      left="250px"
      width="calc(96vw - 250px)"
      height="85vh"
      p={6}
      overflowY="auto"
      bg="white"
    >
      <Box width={450}>
        <fieldset disabled={!pagePermissions?.isEditable}>
          <Text mb="10px" color="blue" textAlign="left">
            {t('features.InvestmentScreening.PricingAgreementForm.label.projectName')} : {(projectInfo?.revisedOpportunityNameByRevision ?? "") !== "" ? projectInfo?.revisedOpportunityNameByRevision : projectInfo?.opportunityName}
          </Text>

          {/* 単価の取り決め */}

          <Box border="1px solid" borderColor="gray.200" borderRadius="md" pt={1} pb={4} pr={4} pl={8} mb={4} boxShadow="md">
            <FormLabel style={labelStyle} mt={0}>{t('features.InvestmentScreening.PricingAgreementForm.category.pricing')}</FormLabel>
            <RadioGroup
              value={pricingAgreementFormValue?.pricing.pricingType}
              onChange={(value => { changePricingAgreementFormValue("pricing.pricingType", value) })}
            >
              <Stack spacing={2}>
                <Radio w="fit-content" isInvalid={false} value={pricingType.FirstTimeTransactionOnly.value}>{t(`features.InvestmentScreening.PricingAgreementForm.options.pricingType.${pricingType.FirstTimeTransactionOnly.value}`)}</Radio>
                <Flex alignItems="flex-start">
                  <Radio w="fit-content" mt="9px" mr="4px" isInvalid={false} value={pricingType.FixedPeriod.value}>{t(`features.InvestmentScreening.PricingAgreementForm.options.pricingType.${pricingType.FixedPeriod.value}`)}</Radio>
                  <VStack align="left" ml="10px">
                    <FormControl isInvalid={errors?.pricingDuration.isError}>
                      <HStack>
                        <CustomNumberInput
                          min={1}
                          max={999}
                          placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.months')}
                          w="65px"
                          value={pricingAgreementFormValue?.pricing.pricingDuration}
                          onChange={(value) => changePricingAgreementFormValue("pricing.pricingDuration", value)}
                        />
                        <Text>{t('features.InvestmentScreening.PricingAgreementForm.label.monthsPeriod')}</Text>
                      </HStack>
                      <FormErrorMessage style={errorMessageStyle}>{errors?.pricingDuration.errorMessage}</FormErrorMessage>
                    </FormControl>
                  </VStack>
                </Flex>
                <Radio w="fit-content" isInvalid={false} value={pricingType.NoArrangement.value}>{t(`features.InvestmentScreening.PricingAgreementForm.options.pricingType.${pricingType.NoArrangement.value}`)}</Radio>
                <Radio w="fit-content" isInvalid={false} value={pricingType.Other.value}>{t(`features.InvestmentScreening.PricingAgreementForm.options.pricingType.${pricingType.Other.value}`)}</Radio>
              </Stack>
            </RadioGroup>
            <Flex wrap="wrap" gap={2} mt={5} ml={0}>
              <FormControl isInvalid={errors?.pricingComment.isError}>
                <Textarea
                  w="500px"
                  placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.freeNotes')}
                  value={pricingAgreementFormValue?.pricing.pricingComment || ""}
                  onChange={(e) => changePricingAgreementFormValue("pricing.pricingComment", e.target.value)}
                ></Textarea>
                <FormErrorMessage style={errorMessageStyle}>{errors?.pricingComment.errorMessage}</FormErrorMessage>
              </FormControl>
            </Flex>
          </Box>

          {/* 為替 */}

          <Box border="1px solid" borderColor="gray.200" borderRadius="md" pt={1} pb={4} pr={4} pl={8} mb={4} boxShadow="md">
            <FormLabel style={labelStyle} mt={0}>{t('features.InvestmentScreening.PricingAgreementForm.category.exchange')}</FormLabel>
            <RadioGroup
              value={pricingAgreementFormValue?.exchange.exchangeType}
              onChange={(value => { changePricingAgreementFormValue("exchange.exchangeType", value) })}
            >
              <Stack spacing={2}>
                <Flex alignItems="flex-start">
                  <Radio w="fit-content" mt="9px" mr="8px" isInvalid={false} value={exchangeType.SubjectToFluctuations.value}>{t(`features.InvestmentScreening.PricingAgreementForm.options.exchangeType.${exchangeType.SubjectToFluctuations.value}`)}</Radio>
                  <Text mt="9px" >{t('features.InvestmentScreening.PricingAgreementForm.label.review')}</Text>
                  <VStack align="left" ml="10px">
                    <FormControl isInvalid={errors?.exchangeDuration.isError}>
                      <HStack>
                        <CustomNumberInput
                          min={1}
                          max={999}
                          placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.months')}
                          w="65px"
                          value={pricingAgreementFormValue?.exchange.exchangeDuration}
                          onChange={(value) => changePricingAgreementFormValue("exchange.exchangeDuration", value)}
                        />
                        <Text>{t('features.InvestmentScreening.PricingAgreementForm.label.monthsEvery')}</Text>

                      </HStack>
                      <FormErrorMessage style={errorMessageStyle}>{errors?.exchangeDuration.errorMessage}</FormErrorMessage>
                    </FormControl>
                  </VStack>
                  <VStack align="left" ml="10px">
                    <FormControl isInvalid={errors?.exchangeThreshold.isError}>
                      <HStack>
                        <CustomNumberInput
                          min={1}
                          max={100}
                          placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.percent')}
                          w="65px"
                          value={pricingAgreementFormValue?.exchange.exchangeThreshold}
                          onChange={(value) => changePricingAgreementFormValue("exchange.exchangeThreshold", value)}
                        />
                        <Text>{t('features.InvestmentScreening.PricingAgreementForm.label.percentOver')}</Text>
                      </HStack>
                      <FormErrorMessage style={errorMessageStyle}>{errors?.exchangeThreshold.errorMessage}</FormErrorMessage>
                    </FormControl>
                  </VStack>
                </Flex>
                <Radio w="fit-content" isInvalid={false} value={exchangeType.NoArrangement.value}>{t(`features.InvestmentScreening.PricingAgreementForm.options.exchangeType.${exchangeType.NoArrangement.value}`)}</Radio>
                <Radio w="fit-content" isInvalid={false} value={exchangeType.Other.value}>{t(`features.InvestmentScreening.PricingAgreementForm.options.exchangeType.${exchangeType.Other.value}`)}</Radio>
              </Stack>
            </RadioGroup>
            <Flex wrap="wrap" gap={2} mt={5} ml={0}>
              <FormControl isInvalid={errors?.exchangeComment.isError}>
                <Textarea
                  w="500px"
                  placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.freeNotes')}
                  value={pricingAgreementFormValue?.exchange.exchangeComment || ""}
                  onChange={(e) => changePricingAgreementFormValue("exchange.exchangeComment", e.target.value)}
                ></Textarea>
                <FormErrorMessage style={errorMessageStyle}>{errors?.exchangeComment.errorMessage}</FormErrorMessage>
              </FormControl>
            </Flex>
          </Box>

          {/* 材料費 */}

          <Box border="1px solid" borderColor="gray.200" borderRadius="md" pt={1} pb={4} pr={4} pl={8} mb={4} boxShadow="md">
            <FormLabel style={labelStyle} mt={0}>{t('features.InvestmentScreening.PricingAgreementForm.category.material')}</FormLabel>
            <RadioGroup
              value={pricingAgreementFormValue?.material.materialType}
              onChange={(value => { changePricingAgreementFormValue("material.materialType", value) })}
            >
              <Stack spacing={2}>
                <Flex alignItems="flex-start">
                  <Radio w="fit-content" mt="9px" mr="4px" flexShrink={0} whiteSpace="nowrap" isInvalid={false} value={materialType.SubjectToFluctuations.value}>{t(`features.InvestmentScreening.PricingAgreementForm.options.materialType.${materialType.SubjectToFluctuations.value}`)}</Radio>
                  <VStack align="left" ml="10px">
                    <FormControl isInvalid={errors?.selectedMaterials.isError}>
                      <Button
                        // size="sm"
                        onClick={handleOpenMaterialModal}
                        isDisabled={pricingAgreementFormValue?.material.materialType !== materialType.SubjectToFluctuations.value}
                        borderColor="red.500"
                        border={errors?.selectedMaterials.isError ? "2px solid red" : "none"}
                      >
                        {t('features.InvestmentScreening.PricingAgreementForm.label.materialSelect')}
                      </Button>
                      <FormErrorMessage style={errorMessageStyle}>{errors?.selectedMaterials.errorMessage}</FormErrorMessage>
                    </FormControl>
                    {/* 選択済み材料の表示 */}
                    {pricingAgreementFormValue.material.selectedMaterials.length === 0
                      && (!pricingAgreementFormValue.material.otherMaterials || pricingAgreementFormValue.material.otherMaterials.length === 0) ? (
                      <Text fontSize="sm" color="gray.400" mt={1}>{t('features.InvestmentScreening.PricingAgreementForm.label.notSelected')}</Text>
                    ) : (
                      <Wrap spacing={2} mt={2}>
                        {pricingAgreementFormValue.material.selectedMaterials.map((materialId: string) => {
                          const mat = materials.find((m: Material) => m.materialId === materialId);
                          if (!mat?.materialName) return null;
                          return (
                            <WrapItem key={materialId}>
                              <Tag size="sm" colorScheme="blue" variant="subtle">
                                <TagLabel>{getMultilingualDisplayName(mat.materialName)}</TagLabel>
                                <TagCloseButton color="red.500" opacity={0.8} _hover={{ opacity: 1 }} onClick={() => {
                                  setPricingAgreementFormValue((prev) => ({
                                    ...prev,
                                    material: {
                                      ...prev.material,
                                      selectedMaterials: prev.material.selectedMaterials.filter((id: string) => id !== materialId),
                                    },
                                  }));
                                }} />
                              </Tag>
                            </WrapItem>
                          );
                        })}
                        {pricingAgreementFormValue.material.otherMaterials
                          ?.map((m: BilingualName, originalIndex: number) => ({ m, originalIndex }))
                          .filter(({ m }) => hasAnyLangValue(m))
                          .map(({ m, originalIndex }) => (
                            <WrapItem key={`other-${originalIndex}`}>
                              <Tag size="sm" colorScheme="green" variant="subtle">
                                <TagLabel>{getMultilingualDisplayName(m)}</TagLabel>
                                <TagCloseButton color="red.500" opacity={0.8} _hover={{ opacity: 1 }} onClick={() => {
                                  setPricingAgreementFormValue((prev) => {
                                    const others = prev.material.otherMaterials.filter((_, i) => i !== originalIndex);
                                    return {
                                      ...prev,
                                      material: { ...prev.material, otherMaterials: others },
                                    };
                                  });
                                }} />
                              </Tag>
                            </WrapItem>
                          ))}
                      </Wrap>
                    )}
                  </VStack>
                </Flex>
                <Radio w="fit-content" isInvalid={false} value={materialType.NoArrangement.value}>{t(`features.InvestmentScreening.PricingAgreementForm.options.materialType.${materialType.NoArrangement.value}`)}</Radio>
                <Radio w="fit-content" isInvalid={false} value={materialType.Other.value}>{t(`features.InvestmentScreening.PricingAgreementForm.options.materialType.${materialType.Other.value}`)}</Radio>
              </Stack>
            </RadioGroup>
            <Flex wrap="wrap" gap={2} mt={5} ml={0}>
              <FormControl isInvalid={errors?.materialComment.isError}>
                <Textarea
                  w="500px"
                  placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.freeNotes')}
                  value={pricingAgreementFormValue?.material.materialComment || ""}
                  onChange={(e) => changePricingAgreementFormValue("material.materialComment", e.target.value)}
                ></Textarea>
                <FormErrorMessage style={errorMessageStyle}>{errors?.materialComment.errorMessage}</FormErrorMessage>
              </FormControl>
            </Flex>
          </Box>

          {/* 数量 */}

          <Box border="1px solid" borderColor="gray.200" borderRadius="md" pt={1} pb={4} pr={4} pl={8} mb={4} boxShadow="md">
            <FormLabel style={labelStyle} mt={0}>{t('features.InvestmentScreening.PricingAgreementForm.category.quantity')}</FormLabel>
            <RadioGroup
              value={pricingAgreementFormValue?.quantity.quantityType}
              onChange={(value => {
                changePricingAgreementFormValue("quantity.quantityType", value);
                if (value === quantityType.Arrangement.value) {
                  if (!pricingAgreementFormValue.quantity.quantitySubType) {
                    pricingAgreementFormValue.quantity.quantitySubType = quantitySubType.NoExpectation.value;
                  }
                }
              })}
            >
              <Stack spacing={2}>
                <Radio w="fit-content" mt="9px" mr="4px" isInvalid={false} value={quantityType.Arrangement.value}>{t(`features.InvestmentScreening.PricingAgreementForm.options.quantityType.${quantityType.Arrangement.value}`)}</Radio>
                {pricingAgreementFormValue?.quantity.quantityType === quantityType.Arrangement.value && (
                  <Flex alignItems="flex-start">
                    <VStack align="left" ml="10px" gap={0}>
                      <FormLabel fontWeight="bold" mt={0} ml={5}>{t('features.InvestmentScreening.PricingAgreementForm.category.quantityNotReached')}</FormLabel>
                      <RadioGroup gap={0} ml={5}
                        value={pricingAgreementFormValue?.quantity.quantitySubType || ""}
                        onChange={(value => { changePricingAgreementFormValue("quantity.quantitySubType", value) })}
                      >
                        <VStack align="start" gap={0}>
                          <Radio w="fit-content" mt="0px" mr="4px" isInvalid={false}
                            value={quantitySubType.Preliminary.value}><Text whiteSpace="nowrap">{t(`features.InvestmentScreening.PricingAgreementForm.options.quantitySubType.${quantitySubType.Preliminary.value}`)}</Text></Radio>
                          {pricingAgreementFormValue?.quantity.quantitySubType === quantitySubType.Preliminary.value && (
                            <VStack align="start" ml="24px" mt={2} gap={2}>
                              <FormControl isInvalid={errors?.quantityDuration.isError} w="fit-content">
                                <HStack gap={0}>
                                  <CustomNumberInput
                                    min={1}
                                    max={999}
                                    placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.months')}
                                    w="65px"
                                    value={pricingAgreementFormValue?.quantity.quantityDuration}
                                    onChange={(value) => changePricingAgreementFormValue("quantity.quantityDuration", value)}
                                  />
                                  <Text whiteSpace="nowrap" ml={2}>{t('features.InvestmentScreening.PricingAgreementForm.label.months')}</Text>
                                </HStack>
                                <FormErrorMessage style={errorMessageStyle}>{errors?.quantityDuration.errorMessage}</FormErrorMessage>
                              </FormControl>
                              <Textarea
                                w={500}
                                placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.freeNotesNotReached')}
                                value={pricingAgreementFormValue?.quantity.notReachedComment || ""}
                                onChange={(e) => changePricingAgreementFormValue("quantity.notReachedComment", e.target.value)}
                              ></Textarea>
                            </VStack>
                          )}
                        </VStack>

                        <Radio w="fit-content" mt="9px" mr="4px" isInvalid={false}
                          value={quantitySubType.NoExpectation.value}>{t(`features.InvestmentScreening.PricingAgreementForm.options.quantitySubType.${quantitySubType.NoExpectation.value}`)}</Radio>
                      </RadioGroup>
                    </VStack>
                  </Flex>
                )}
                <Radio w="fit-content" isInvalid={false} value={quantityType.NoArrangement.value}>{t(`features.InvestmentScreening.PricingAgreementForm.options.quantityType.${quantityType.NoArrangement.value}`)}</Radio>
                <Radio w="fit-content" isInvalid={false} value={quantityType.Other.value}>{t(`features.InvestmentScreening.PricingAgreementForm.options.quantityType.${quantityType.Other.value}`)}</Radio>
              </Stack>
            </RadioGroup>
            <Flex wrap="wrap" gap={2} mt={5} ml={0}>
              <FormControl isInvalid={errors?.quantityComment.isError}>
                <Textarea
                  w="500px"
                  placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.freeNotes')}
                  value={pricingAgreementFormValue?.quantity.quantityComment || ""}
                  onChange={(e) => changePricingAgreementFormValue("quantity.quantityComment", e.target.value)}
                ></Textarea>
                <FormErrorMessage style={errorMessageStyle}>{errors?.quantityComment.errorMessage}</FormErrorMessage>
              </FormControl>
            </Flex>
          </Box>

          {/* 競合 */}

          <Box border="1px solid" borderColor="gray.200" borderRadius="md" pt={1} pb={4} pr={4} pl={8} mb={4} boxShadow="md">
            <FormLabel style={labelStyle} mt={0}>{t('features.InvestmentScreening.PricingAgreementForm.category.competitor')}</FormLabel>
            <RadioGroup
              value={pricingAgreementFormValue?.competitor.competitorType}
              onChange={(value => { changePricingAgreementFormValue("competitor.competitorType", value) })}
            >
              <Stack spacing={2}>
                <Radio w="fit-content" mt="9px" mr="4px" isInvalid={false} value={competitorType.Competitor.value}>{t(`features.InvestmentScreening.PricingAgreementForm.options.competitorType.${competitorType.Competitor.value}`)}</Radio>
                {pricingAgreementFormValue?.competitor.competitorType === competitorType.Competitor.value && (
                  <Flex alignItems="flex-start">
                    <VStack align="left" ml="10px" gap={0}>
                      <FormLabel fontWeight="bold" mt={0} ml={5}>{t('features.InvestmentScreening.PricingAgreementForm.category.selectedCompetitors')}</FormLabel>
                      <FormControl isInvalid={errors?.selectedCompetitors.isError}>
                        <Flex wrap="wrap" gap={2} mt={0} ml={5}>
                          {(() => {
                            return (
                              <>
                                {competitorCandidatesForDept.map(competitor => {
                                  if (!competitor.competitorId || !competitor.competitorName) return null;
                                  const isSelected = selectedCompetitorIds.includes(competitor.competitorId);
                                  return (
                                    <Button
                                      key={competitor.competitorId}
                                      h="28px"
                                      px="8px"
                                      border={errors?.selectedCompetitors.isError ? "2px solid red" : "2px solid transparent"}
                                      borderRadius="4px"
                                      bg={isSelected ? "#2a4365" : "#ccc"}
                                      color={isSelected ? "#fff" : "#000"}
                                      opacity={selectedCompetitorIds.includes(UNCONFIRMED) ? 0.5 : 1}
                                      fontSize="12px"
                                      cursor={selectedCompetitorIds.includes(UNCONFIRMED) ? "not-allowed" : "pointer"}
                                      onClick={() => {
                                        // 競合未確認が選択状態の場合は、処理を抜ける
                                        if (selectedCompetitorIds.includes(UNCONFIRMED)) return;
                                        // 競合IDまたは競合名がない場合は、処理を抜ける
                                        if (!competitor.competitorId || !competitor.competitorName) return;
                                        const newIds = [...selectedCompetitorIds];
                                        if (newIds.includes(competitor.competitorId)) {
                                          // 選択状態の場合は、未選択状態にする
                                          newIds.splice(newIds.indexOf(competitor.competitorId), 1);
                                        } else {
                                          // 未選択状態の場合は、選択状態にする
                                          newIds.push(competitor.competitorId);
                                        }
                                        // 選択中の競合会社を更新する
                                        setSelectedCompetitorIds(newIds);
                                        // 顧客取り決め事項情報を更新する
                                        changePricingAgreementFormValue("competitor.selectedCompetitors", newIds)
                                      }}
                                    >
                                      {competitor.competitorName}
                                    </Button>
                                  );
                                })}
                                <HStack gap={2}>
                                  <Button
                                    h="28px"
                                    px="8px"
                                    border={errors?.selectedCompetitors?.isError ? "2px solid red" : "2px solid transparent"}
                                    borderRadius="4px"
                                    bg={selectedCompetitorIds.includes(OTHER) ? "#2a4365" : "#ccc"}
                                    color={selectedCompetitorIds.includes(OTHER) ? "#fff" : "#000"}
                                    opacity={selectedCompetitorIds.includes(UNCONFIRMED) ? 0.5 : 1}
                                    fontSize="12px"
                                    cursor={selectedCompetitorIds.includes(UNCONFIRMED) ? "not-allowed" : "pointer"}
                                    onClick={() => {
                                      // 競合未確認が選択状態の場合は、処理を抜ける
                                      if (selectedCompetitorIds.includes(UNCONFIRMED)) return;
                                      const newIds = [...selectedCompetitorIds];
                                      if (selectedCompetitorIds.includes(OTHER)) {
                                        // 選択状態の場合は、未選択状態にする
                                        newIds.splice(newIds.indexOf(OTHER), 1);
                                      } else {
                                        // 未選択状態の場合は、選択状態にする
                                        newIds.push(OTHER);
                                      }
                                      // 選択中の競合会社を更新する
                                      setSelectedCompetitorIds(newIds);
                                      // 顧客取り決め事項情報を更新する
                                      changePricingAgreementFormValue("competitor.selectedCompetitors", newIds);
                                    }}
                                  >
                                    {t('features.InvestmentScreening.PricingAgreementForm.label.otherCategory')}
                                  </Button>
                                  <Button
                                    h="28px"
                                    px="8px"
                                    border={errors?.selectedCompetitors?.isError ? "2px solid red" : "2px solid transparent"}
                                    borderRadius="4px"
                                    bg={selectedCompetitorIds.includes(UNCONFIRMED) ? "#2a4365" : "#ccc"}
                                    color={selectedCompetitorIds.includes(UNCONFIRMED) ? "#fff" : "#000"}
                                    fontSize="12px"
                                    cursor="pointer"
                                    onClick={() => {
                                      const newIds = [...selectedCompetitorIds];
                                      if (selectedCompetitorIds.includes(UNCONFIRMED)) {
                                        // 選択状態の場合は、未選択状態にする
                                        newIds.splice(newIds.indexOf(UNCONFIRMED), 1);
                                      } else {
                                        // 未選択状態の場合は、他の競合会社を全て選択解除した後に選択状態にする
                                        newIds.splice(0, newIds.length);
                                        newIds.push(UNCONFIRMED);
                                      }
                                      // 選択中の競合会社を更新する
                                      setSelectedCompetitorIds(newIds);
                                      // 顧客取り決め事項情報を更新する
                                      changePricingAgreementFormValue("competitor.selectedCompetitors", newIds);
                                      changePricingAgreementFormValue("competitor.competitorNameOther", null)
                                    }}
                                  >
                                    {t('features.InvestmentScreening.PricingAgreementForm.label.competitorUnconfirmed')}
                                  </Button>
                                </HStack>
                                {selectedCompetitorIds.includes(OTHER) && (
                                  <Input
                                    h="28px"
                                    w="300px"
                                    border="1px solid #ccc"
                                    placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.competitorNameOther')}
                                    value={pricingAgreementFormValue?.competitor.competitorNameOther || ""}
                                    onChange={(e) => {
                                      // 顧客取り決め事項情報を更新する
                                      changePricingAgreementFormValue("competitor.competitorNameOther", e.target.value);
                                    }}
                                  ></Input>
                                )}
                              </>
                            );
                          })()}
                        </Flex>
                        {pricingAgreementFormValue?.competitor.competitorType === competitorType.Competitor.value && (
                          <FormErrorMessage ml={20} style={{ ...errorMessageStyle, marginTop: "5px", marginLeft: "20px" }}>
                            {errors?.selectedCompetitors.errorMessage}
                          </FormErrorMessage>
                        )}
                      </FormControl>

                      {/* 競合状態 */}
                      <FormLabel fontWeight="bold" mt={3} ml={5}>{t('features.InvestmentScreening.PricingAgreementForm.category.competitiorCondition')}</FormLabel>
                      <RadioGroup
                        value={pricingAgreementFormValue?.competitor?.competitiorCondition || ""}
                        onChange={(value => {
                          changePricingAgreementFormValue("competitor.competitiorCondition", value);
                        })}
                      >
                        <Stack spacing={2} direction="row" ml={9}>
                          {Object.values(competitiorCondition).map(condition => (
                            <Radio key={condition.value} value={condition.value}>
                              {t(`features.InvestmentScreening.PricingAgreementForm.options.competitiorCondition.${condition.value}`)}
                            </Radio>
                          ))}
                        </Stack>
                      </RadioGroup>

                      <FormLabel fontWeight="bold" mt={3} ml={5}>{t('features.InvestmentScreening.PricingAgreementForm.category.shareOfCompetitorAndCompany')}</FormLabel>
                      {/* 当社シェア数値入力欄（競合あり） */}
                      <FormControl isInvalid={errors?.sharePercentage?.isError} ml={5} mb={2}>
                        <HStack>
                          <Text fontSize="sm">{t('features.InvestmentScreening.PricingAgreementForm.label.companyShare')}</Text>
                          <CustomNumberInput
                            min={0}
                            max={100}
                            decimalPlaces={1}
                            placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.percent')}
                            w="80px"
                            value={pricingAgreementFormValue?.competitor.sharePercentage}
                            onChange={(value) => changePricingAgreementFormValue("competitor.sharePercentage", value)}
                          />
                          <Text>{t('features.InvestmentScreening.PricingAgreementForm.placeholder.percent')}</Text>
                        </HStack>
                        <FormErrorMessage style={errorMessageStyle}>{errors?.sharePercentage?.errorMessage}</FormErrorMessage>
                      </FormControl>
                      <Flex wrap="wrap" gap={2} mt={0} ml={5}>
                        <Textarea
                          w="500px"
                          placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.freeNotes')}
                          value={pricingAgreementFormValue?.competitor.shareOfCompetitorAndCompany || ""}
                          onChange={(e) => changePricingAgreementFormValue("competitor.shareOfCompetitorAndCompany", e.target.value)}
                        ></Textarea>
                      </Flex>
                    </VStack>
                  </Flex>
                )}
                <Radio w="fit-content" isInvalid={false} value={competitorType.NoCompetitor.value}>{t(`features.InvestmentScreening.PricingAgreementForm.options.competitorType.${competitorType.NoCompetitor.value}`)}</Radio>
              </Stack>
            </RadioGroup>
          </Box>

          {/* その他の取り決め */}

          <Box border="1px solid" borderColor="gray.200" borderRadius="md" pt={1} pb={4} pr={4} pl={8} mb={4} boxShadow="md">
            <FormLabel style={labelStyle} mt={0}>{t('features.InvestmentScreening.PricingAgreementForm.label.otherAgreements')}</FormLabel>
            <VStack align="stretch" spacing={4} mb={4}>
              {otherAgreements.map((item, index) => (
                <Box key={index} p={3} borderWidth="1px" borderColor="gray.200" borderRadius="md">
                  <HStack mb={2} align="center">
                    <Text fontSize="sm" fontWeight="bold" flexShrink={0} w="60px">{t('features.InvestmentScreening.PricingAgreementForm.label.titleLabel')}</Text>
                    <Input
                      placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.titleInput')}
                      value={item.title || ""}
                      onChange={(e) => changeOtherAgreementTitle(index, e.target.value)}
                      maxLength={100}
                    />
                    <IconButton
                      aria-label={t('features.InvestmentScreening.PricingAgreementForm.button.delete')}
                      icon={<RiDeleteBin6Fill />}
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => removeOtherAgreement(index)}
                    />
                  </HStack>
                  <HStack align="flex-start">
                    <Text fontSize="sm" fontWeight="bold" flexShrink={0} w="60px" mt="8px">{t('features.InvestmentScreening.PricingAgreementForm.label.contentLabel')}</Text>
                    <Textarea
                      placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.freeNotes')}
                      value={item.content || ""}
                      onChange={(e) => changeOtherAgreementContent(index, e.target.value)}
                      width="500px"
                    />
                  </HStack>
                </Box>
              ))}
              <Box>
                <Button
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  leftIcon={<FaPlusCircle />}
                  onClick={addOtherAgreement}
                >
                  {t('features.InvestmentScreening.PricingAgreementForm.button.add')}
                </Button>
              </Box>
            </VStack>
          </Box>

          {/* 顧客国籍 */}

          <Box border="1px solid" borderColor="gray.200" borderRadius="md" pt={1} pb={4} pr={4} pl={8} mb={4} boxShadow="md">
            <Flex align="center" gap={1} mt={0} mb={2}>
              <FormLabel style={labelStyle} mt={0} mb={0}>{t('features.InvestmentScreening.PricingAgreementForm.label.customerNationality')}</FormLabel>
              <HelpIcon width="420px">
                <Text>
                  {t('features.InvestmentScreening.PricingAgreementForm.help.customerNationality')}
                </Text>
              </HelpIcon>
            </Flex>

            <Select width={350} value={customerNationality ?? ""} onChange={(e) => { setCustomerNationality(e.target.value) }}>
              {nationalityList.map((n) => (
                <option key={n.nationalityId} value={n.nationalityId}>
                  {getBilingualDisplayName(n, 'nationalityNameJpn', 'nationalityNameEng')}
                </option>
              ))}
            </Select>
          </Box>

          {/* ファイルアップロード */}

          <Box border="1px solid" borderColor="gray.200" borderRadius="md" pt={1} pb={4} pr={4} pl={8} mb={4} boxShadow="md">
            <FormLabel style={labelStyle} mt={0}>{t('features.InvestmentScreening.PricingAgreementForm.label.fileUpload')}</FormLabel>
            <Text mb={2} color="red">{t('features.InvestmentScreening.PricingAgreementForm.label.fileUploadNote')}</Text>
            <RadioGroup
              onChange={(value) => {
                const isUpload = value === "true";
                changePricingAgreementFormValue("uploadOption", isUpload);
                if (value === uploadOptionType.NoUpload.value.toString()) {
                  uploadedFiles.forEach((file) => handleDelete(file));
                }
              }}
              value={(pricingAgreementFormValue?.uploadOption ?? true).toString()}
            >
              <Stack spacing={2}>
                <Flex alignItems="flex-start">
                  <Radio w="fit-content" mt="9px" mr="4px" isInvalid={false} value={uploadOptionType.Upload.value.toString()}>
                    {t('features.InvestmentScreening.PricingAgreementForm.options.uploadOptionType.Upload')}
                  </Radio>
                  <VStack align="left" ml="10px">
                    <Box>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        multiple
                        onChange={handleFileSelect}
                      />
                      <FormControl isInvalid={errors?.selectedUploadFiles.isError}>
                        <Box position="relative" display="inline-block">
                          <Button onClick={() => fileInputRef.current?.click()} isDisabled={!(pricingAgreementFormValue?.uploadOption ?? true)}>
                            {t('features.InvestmentScreening.PricingAgreementForm.label.fileSelect')}
                          </Button>
                          {uploadOptionType.Upload.value && (
                            <FormErrorMessage ml={20} style={errorMessageStyle}>{errors?.selectedUploadFiles.errorMessage}</FormErrorMessage>
                          )}
                        </Box>
                      </FormControl>
                    </Box>
                  </VStack>
                </Flex>
                <Radio value={uploadOptionType.NoUpload.value.toString()}>
                  {t('features.InvestmentScreening.PricingAgreementForm.options.uploadOptionType.NoUpload')}
                </Radio>
                <FileList files={uploadedFiles ?? []} onDelete={handleDelete} />
              </Stack>
            </RadioGroup>
          </Box>
        </fieldset>
      </Box >
      {/* 確認ボタン */}
      < Flex justify="flex-end" mt="20px" >
        <Box>
          <Button
            ml="10px"
            colorScheme="blue"
            name="companyDivision"
            onClick={handleConfirm}
            isDisabled={hasfetchException || !pagePermissions?.eabledCompanyDivision}
            ref={refs["companyDivision"]}
          >
            {t('features.InvestmentScreening.PricingAgreementForm.button.companyDivisionConfirm')}
          </Button>
          <ConfirmorList confirmorInfo={names["companyDivision"]} width={buttonWidths["companyDivision"]} />
        </Box>
        <Box>
          <Button
            ml="10px"
            colorScheme="blue"
            name="salesDivision"
            onClick={handleConfirm}
            isDisabled={hasfetchException || !pagePermissions?.eabledSalesDivision}
            ref={refs["salesDivision"]}
          >
            {t('features.InvestmentScreening.PricingAgreementForm.button.salesDivisionConfirm')}
          </Button>
          <ConfirmorList confirmorInfo={names["salesDivision"]} width={buttonWidths["salesDivision"]} />
        </Box>
        <Box>
          <Button
            ml="10px"
            colorScheme="blue"
            name="procurementDivision"
            onClick={handleConfirm}
            isDisabled={hasfetchException || !pagePermissions?.eabledProcurementDivision}
            ref={refs["procurementDivision"]}
          >
            {t('features.InvestmentScreening.PricingAgreementForm.button.procurementDivisionConfirm')}
          </Button>
          <ConfirmorList confirmorInfo={names["procurementDivision"]} width={buttonWidths["procurementDivision"]} />
        </Box>
      </Flex >

      {/* 材料モーダル */}
      < Modal isOpen={isMaterialModalOpen} onClose={onMaterialModalClose} >
        <ModalOverlay />
        <ModalContent maxW="700px">
          <ModalHeader>{t('features.InvestmentScreening.PricingAgreementForm.modal.materialTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            {/* カテゴリ別にグルーピング表示（削除済みを除く） */}
            {Array.from(
              new Set(
                materials
                  .filter((m: Material) => !m.deleted && m.category)
                  .map((m: Material) => categoryKeyOf(m.category)!)
              )
            ).map((categoryKey: string) => {
              const category: BilingualName = JSON.parse(categoryKey);
              return (
                <Box key={categoryKey} mb={4} border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={3}>{getMultilingualDisplayName(category)}</Text>
                  <Wrap spacing={4} ml={2}>
                    {materials
                      .filter((m: Material) => categoryKeyOf(m.category) === categoryKey && !m.deleted)
                      .map((material: Material) => (
                        <WrapItem key={material.materialId}>
                          <Checkbox
                            isChecked={tempSelectedMaterials.includes(material.materialId!)}
                            onChange={() => handleMaterialSelection(material.materialId!)}
                          >
                            {getMultilingualDisplayName(material.materialName)}
                          </Checkbox>
                        </WrapItem>
                      ))}
                  </Wrap>
                </Box>
              );
            })}
            {/* カテゴリなしの材料（既存データ互換、削除済みを除く） */}
            {materials.filter((m: Material) => !m.category && !m.deleted).length > 0 && (
              <Box mb={4} border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                <Wrap spacing={4}>
                  {materials
                    .filter((m: Material) => !m.category && !m.deleted)
                    .map((material: Material) => (
                      <WrapItem key={material.materialId}>
                        <Checkbox
                          isChecked={tempSelectedMaterials.includes(material.materialId!)}
                          onChange={() => handleMaterialSelection(material.materialId!)}
                        >
                          {getMultilingualDisplayName(material.materialName)}
                        </Checkbox>
                      </WrapItem>
                    ))}
                </Wrap>
              </Box>
            )}
            {/* その他（自由入力） */}
            <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
              <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={3}>{t('features.InvestmentScreening.PricingAgreementForm.label.otherCategory')}</Text>
              <VStack align="stretch" spacing={2} ml={2}>
                {tempOtherMaterials.map((item, index) => (
                  <HStack key={index}>
                    {langs.map((lang) => {
                      const CapitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);
                      const placeholderTranslationKey = lang === 'ja'
                        ? 'features.InvestmentScreening.PricingAgreementForm.placeholder.materialNameInputJa'
                        : `features.InvestmentScreening.PricingAgreementForm.placeholder.materialNameInput${CapitalizedLang}`;
                      return (
                        <Input
                          key={`other-material-input-${lang}-${index}`}
                          placeholder={t(placeholderTranslationKey)}
                          value={item[lang] || ""}
                          onChange={(e) => {
                            setTempOtherMaterials(prev =>
                              prev.map((v, i) => i === index ? { ...v, [lang]: e.target.value } : v)
                            );
                          }}
                        />
                      );
                    })}
                    <IconButton
                      aria-label={t('features.InvestmentScreening.PricingAgreementForm.button.delete')}
                      icon={<RiDeleteBin6Fill />}
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => {
                        setTempOtherMaterials(prev => prev.filter((_, i) => i !== index));
                      }}
                    />
                  </HStack>
                ))}
                <Box>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    leftIcon={<FaPlusCircle />}
                    onClick={() => setTempOtherMaterials(prev => [
                      ...prev,
                      langs.reduce((acc, lang) => { acc[lang] = ""; return acc; }, {} as BilingualName),
                    ])}
                  >
                    {t('features.InvestmentScreening.PricingAgreementForm.button.add')}
                  </Button>
                </Box>
              </VStack>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleConfirmMaterialSelection}>
              {t('features.InvestmentScreening.PricingAgreementForm.button.confirm')}
            </Button>
            <Button variant="ghost" onClick={onMaterialModalClose}>
              {t('features.InvestmentScreening.PricingAgreementForm.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal >

      {/* 確認モーダル */}
      < Modal isOpen={isConfirmationModalOpen} onClose={onConfirmationModalClose} size="xl" >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('features.InvestmentScreening.PricingAgreementForm.modal.confirmationTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" maxH={640}>
            <VStack align="stretch" spacing={4}>
              {/* 単価の取り決め */}
              <Flex align="flex-start">
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.PricingAgreementForm.category.pricing')}：</Text>
                <Text style={finalConfirmValueStyle} title={confirmData?.pricing}>{confirmData?.pricing}</Text>
              </Flex>
              < Flex align="flex-start">
                <Text style={{ ...finalConfirmLabelStyle, width: "165px" }} ml="15px">{t('features.InvestmentScreening.PricingAgreementForm.placeholder.freeNotes')}</Text>
                <Textarea readOnly width="350px" maxHeight="400px" placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.noInput')} value={confirmData?.pricingComment || ""}></Textarea>
              </Flex>
              {/* 為替変動の取り決め */}
              <Flex align="flex-start" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.PricingAgreementForm.category.exchange')}：</Text>
                <Text style={finalConfirmValueStyle} title={confirmData?.exchange}>{confirmData?.exchange}</Text>
              </Flex>
              < Flex align="flex-start">
                <Text style={{ ...finalConfirmLabelStyle, width: "165px" }} ml="15px">{t('features.InvestmentScreening.PricingAgreementForm.placeholder.freeNotes')}</Text>
                <Textarea readOnly width="350px" maxHeight="400px" placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.noInput')} value={confirmData?.exchangeComment || ""}></Textarea>
              </Flex>
              <VStack align="left" verticalAlign="top">
                {/* 材料費変動の取り決め */}
                <Flex align="flex-start" >
                  <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.PricingAgreementForm.category.material')}：</Text>
                  <Text style={finalConfirmValueStyle} title={confirmData?.material}>{confirmData?.material}</Text>
                </Flex>
                {/* 材料選択 */}
                {pricingAgreementFormValue.material.materialType === materialType.SubjectToFluctuations.value && (
                  <Flex align="flex-start">
                    <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.PricingAgreementForm.label.selectedMaterialsLabel')}</Text>
                    <Text maxW="370px" title={confirmData?.selectMaterial}>{confirmData?.selectMaterial}</Text>
                  </Flex>
                )}
              </VStack>
              < Flex align="flex-start">
                <Text style={{ ...finalConfirmLabelStyle, width: "165px" }} ml="15px">{t('features.InvestmentScreening.PricingAgreementForm.placeholder.freeNotes')}</Text>
                <Textarea readOnly width="350px" maxHeight="400px" placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.noInput')} value={confirmData?.materialComment || ""}></Textarea>
              </Flex>
              <VStack align="left" verticalAlign="top">
                {/* 数量の取り決め */}
                <Flex align="center" >
                  <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.PricingAgreementForm.category.quantity')}：</Text>
                  <Text style={finalConfirmValueStyle} title={confirmData?.quantity}>{confirmData?.quantity}</Text>
                </Flex>
                {/* 数量未達時の取り決め（自由記入欄） */}
                {pricingAgreementFormValue.quantity.quantityType === quantityType.Arrangement.value &&
                  pricingAgreementFormValue.quantity.quantitySubType === quantitySubType.Preliminary.value && (
                    < Flex align="flex-start">
                      <Text style={{ ...finalConfirmLabelStyle, width: "165px" }} ml="15px">{t('features.InvestmentScreening.PricingAgreementForm.label.noteNotReached')}</Text>
                      <Textarea readOnly width="350px" maxHeight="400px" placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.noInput')} value={confirmData?.notReachedComment || ""}></Textarea>
                    </Flex>
                  )}
              </VStack>
              < Flex align="flex-start">
                <Text style={{ ...finalConfirmLabelStyle, width: "165px" }} ml="15px">{t('features.InvestmentScreening.PricingAgreementForm.placeholder.freeNotes')}</Text>
                <Textarea readOnly width="350px" maxHeight="400px" placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.noInput')} value={confirmData?.quantityComment || ""}></Textarea>
              </Flex>
              <VStack align="left" verticalAlign="top">
                {/* 競合の有無 */}
                <Flex align="flex-start" >
                  <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.PricingAgreementForm.category.competitor')}：</Text>
                  <Text style={finalConfirmValueStyle} title={confirmData?.competitor}>{confirmData?.competitor}</Text>
                </Flex>
                {/* 競合状態 */}
                {pricingAgreementFormValue.competitor.competitorType === competitorType.Competitor.value && (
                  <Flex align="flex-start" >
                    <Text style={{ ...finalConfirmLabelStyle, width: "165px" }} ml="15px">{t('features.InvestmentScreening.PricingAgreementForm.category.competitiorCondition')}</Text>
                    <Text style={finalConfirmValueStyle} title={confirmData?.competitiorCondition}>{confirmData?.competitiorCondition}</Text>
                  </Flex>
                )}
                {/* 競合会社選択 */}
                {pricingAgreementFormValue.competitor.competitorType === competitorType.Competitor.value && (
                  <Flex align="flex-start">
                    <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.PricingAgreementForm.label.selectedCompetitorsLabel')}</Text>
                    <Text maxW="370px" title={confirmData?.selectCompetitor}>{confirmData?.selectCompetitor}</Text>
                  </Flex>
                )}
                {/* 当社シェア（競合あり時のみ表示） */}
                {pricingAgreementFormValue.competitor.competitorType === competitorType.Competitor.value && (
                  <Flex align="flex-start">
                    <Text style={{ ...finalConfirmLabelStyle, width: "165px" }} ml="15px">{t('features.InvestmentScreening.PricingAgreementForm.label.companyShare')}</Text>
                    <Text style={finalConfirmValueStyle}>{confirmData?.sharePercentage != null ? `${confirmData.sharePercentage}%` : "―"}</Text>
                  </Flex>
                )}
                {pricingAgreementFormValue.competitor.competitorType === competitorType.Competitor.value && (
                  < Flex align="flex-start">
                    <Text style={{ ...finalConfirmLabelStyle, width: "165px" }} ml="15px">{t('features.InvestmentScreening.PricingAgreementForm.placeholder.freeNotes')}</Text>
                    <Textarea readOnly width="350px" maxHeight="400px" placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.noInput')} value={confirmData?.shareOfCompetitorAndCompany || ""}></Textarea>
                  </Flex>
                )}
              </VStack>
              {/* その他の取り決め */}
              {otherAgreements.length > 0 && (
                <VStack align="stretch" spacing={2} mt={2}>
                  <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.PricingAgreementForm.label.otherAgreements')}：</Text>
                  {otherAgreements.map((item, index) => (
                    <Box key={index} ml="15px">
                      <Text fontWeight="bold" fontSize="sm">{item.title || t('features.InvestmentScreening.PricingAgreementForm.label.titleNone')}</Text>
                      <Textarea readOnly width="350px" maxHeight="400px" placeholder={t('features.InvestmentScreening.PricingAgreementForm.placeholder.noInput')} value={item.content || ""}></Textarea>
                    </Box>
                  ))}
                </VStack>
              )}
              {/* 顧客国籍 */}
              <Flex align="flex-start" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.PricingAgreementForm.label.customerNationality')}：</Text>
                {(() => {
                  const selectedNationality = nationalityList.find(n => n.nationalityId === customerNationality);
                  const nationalityDisplayName = getBilingualDisplayName(selectedNationality, 'nationalityNameJpn', 'nationalityNameEng');
                  return (
                    <Text style={finalConfirmValueStyle} title={nationalityDisplayName}>
                      {nationalityDisplayName}
                    </Text>
                  );
                })()}
              </Flex>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" isDisabled={isSaving} isLoading={isSaving} onClick={(e) => handleSave(e)} mr={3}>
              {t('features.InvestmentScreening.PricingAgreementForm.button.save')}
            </Button>
            <Button variant="ghost" onClick={onConfirmationModalClose}>
              {t('features.InvestmentScreening.PricingAgreementForm.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal >
    </Box >
  );
});

export default PricingAgreementForm;
